'use client';
import React, { useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar, A11y } from 'swiper/modules';
import { motion } from 'motion/react';
import { cn } from 'lib/utils';
import { CarouselNavigation } from 'ui';
import { type ResourceGridChildProps } from './index';
import ResourceCard from './ResourceCard';
import ResourceGridFooterLinks from './ResourceGridFooterLinks';

const ResourceGridMobile = ({
  sections = [],
  activeSection,
  showToggle,
  toggleId,
}: ResourceGridChildProps) => {
  const id = useId().replaceAll(':', ''); // Remove colons as an SSR-friendly alternative to CSS.escape()

  // If no sections, return null
  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <div className="lg:hidden">
      <div className="relative">
        {sections.map((section, sectionIndex) => {
          const isActive = showToggle
            ? sectionIndex === activeSection
            : sectionIndex === 0;
          const shouldRender = showToggle ? true : sectionIndex === 0;

          if (!shouldRender) return null;

          return (
            <motion.div
              key={sectionIndex}
              initial={false}
              animate={{
                x: showToggle ? `${(sectionIndex - activeSection) * 100}%` : 0,
                opacity: isActive ? 1 : 0,
              }}
              transition={{
                duration: 0.4,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className={cn(
                'w-full',
                showToggle && sectionIndex !== activeSection
                  ? 'absolute inset-0 pointer-events-none'
                  : 'relative'
              )}
              role={showToggle ? 'tabpanel' : undefined}
              id={showToggle ? `${toggleId}-panel-${sectionIndex}` : undefined}
              aria-labelledby={
                showToggle ? `${toggleId}-tab-${sectionIndex}` : undefined
              }
              tabIndex={showToggle && isActive ? 0 : undefined}
              aria-hidden={showToggle ? !isActive : false}
              inert={showToggle && !isActive ? true : undefined}
            >
              <Swiper
                modules={[Navigation, Scrollbar, A11y]}
                navigation={{
                  nextEl: `#${id}-next-${sectionIndex}`,
                  prevEl: `#${id}-prev-${sectionIndex}`,
                }}
                scrollbar={{
                  el: `#${id}-scrollbar-${sectionIndex}`,
                  draggable: false,
                }}
                spaceBetween={16}
                slidesPerView={1.15}
                breakpoints={{
                  // when window width is >= 360px
                  360: {
                    slidesPerView: 1.25,
                    spaceBetween: 16,
                  },
                  // when window width is >= 480px
                  480: {
                    slidesPerView: 1.5,
                    spaceBetween: 16,
                  },
                  // when window width is >= 800px
                  800: {
                    slidesPerView: 2.25,
                    spaceBetween: 24,
                  },
                  // when window width is >= 1024px
                  1024: {
                    slidesPerView: 2.75,
                    spaceBetween: 24,
                  },
                }}
                className="z-2"
              >
                {section.items.map((item, index) => (
                  <SwiperSlide key={index} className="!h-auto">
                    <ResourceCard
                      key={index}
                      layout={section.itemsLayout}
                      {...item}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              <CarouselNavigation
                scrollbarId={`${id}-scrollbar-${sectionIndex}`}
                prevId={`${id}-prev-${sectionIndex}`}
                nextId={`${id}-next-${sectionIndex}`}
              />

              <ResourceGridFooterLinks links={section.footerLinks} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ResourceGridMobile;
